import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import NewRequestDialog from "@/components/NewRequestDialog";
import { Clock, Plus, CalendarRange, CheckCircle2, XCircle, Loader2, ArrowRight, Stethoscope, Undo2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { AdjustmentList } from "@/components/permessi/AdjustmentList";

const STATUS_META = {
  pending:   { label: "In attesa",  cls: "bg-amber-50 text-amber-800 border-amber-200",     icon: Loader2 },
  approvato: { label: "Approvato",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  rifiutato: { label: "Rifiutato",  cls: "bg-red-50 text-red-800 border-red-200",           icon: XCircle },
  annullato: { label: "Annullato",  cls: "bg-stone-100 text-stone-600 border-stone-200",     icon: Undo2 },
};

export default function PortalPermessi() {
  const [newOpen, setNewOpen] = useState(false);
  const [newCat, setNewCat] = useState("permesso_assenza");
  const openNew = (c) => { setNewCat(c); setNewOpen(true); };
  const qc = useQueryClient();

  const { data: quota } = useQuery({
    queryKey: ["my-leave-quota"],
    queryFn: async () => (await api.get("/leave-quotas/me")).data,
  });
  const { data: all = [] } = useQuery({
    queryKey: ["my-permessi-requests"],
    queryFn: async () => (await api.get("/requests", { params: { category: "permesso_assenza" } })).data,
  });

  const { data: medical = [] } = useQuery({
    queryKey: ["my-medical-requests"],
    queryFn: async () => (await api.get("/requests", { params: { category: "visita_medica" } })).data,
  });

  const { data: adjustments = [] } = useQuery({
    queryKey: ["my-leave-adjustments"],
    queryFn: async () => (await api.get("/leave-quotas/me/adjustments")).data,
  });

  const totals = useMemo(() => {
    const t = { pending: 0, approved: 0, hoursPending: 0 };
    all.forEach((r) => {
      if (r.status === "pending") { t.pending += 1; t.hoursPending += r.requested_hours || 0; }
      if (r.status === "approvato") t.approved += 1;
    });
    return t;
  }, [all]);

  const pct = quota && quota.total_hours > 0
    ? Math.min(100, ((quota.total_hours - quota.available_hours) / quota.total_hours) * 100)
    : 0;
  const barColor = pct >= 90 ? "#dc2626" : pct >= 70 ? "#f59e0b" : "#1C352D";

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto" data-testid="portal-permessi">
      <PageHeader
        title="Permessi"
        subtitle="Il tuo monte ore e le richieste di permesso. Approvazione: segreteria didattica."
      />

      {/* Saldo */}
      <Card className="p-5 md:p-6 border border-stone-200 rounded-xl shadow-none mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Clock size={11}/> Monte ore permessi</div>
            <div className="text-3xl font-semibold text-[#1C352D] mt-1" data-testid="my-leave-balance">
              {quota?.available_hours ?? 0}h <span className="text-sm text-stone-500 font-normal">disponibili</span>
            </div>
            <div className="text-[12px] text-stone-500 mt-0.5">
              su un totale di <b>{quota?.total_hours || 0}h</b> · usate <b>{quota?.used_hours || 0}h</b>
              {quota?.adjusted_hours ? <> · rettifiche <b>{quota.adjusted_hours > 0 ? "−" : "+"}{Math.abs(quota.adjusted_hours)}h</b></> : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => openNew("permesso_assenza")} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="new-permesso-btn">
              <Plus size={14} className="mr-1"/> Richiedi permesso
            </Button>
            <Button onClick={() => openNew("visita_medica")} variant="outline" className="border-sky-300 text-sky-800 hover:bg-sky-50" data-testid="new-medical-btn">
              <Stethoscope size={14} className="mr-1"/> Visita medica
            </Button>
          </div>
        </div>
        {quota?.total_hours > 0 && (
          <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }}/>
          </div>
        )}
        {totals.pending > 0 && (
          <div className="mt-3 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            {totals.pending} richies{totals.pending === 1 ? "ta" : "te"} in attesa per {totals.hoursPending}h — se approvate il saldo diminuirà.
          </div>
        )}
        {(quota?.total_hours || 0) === 0 && (
          <div className="mt-3 text-[12px] text-stone-500 bg-stone-50 border border-stone-200 rounded-md px-3 py-2">
            La segreteria non ha ancora impostato il tuo monte ore permessi.
          </div>
        )}
      </Card>

      {adjustments.length > 0 && (
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none mb-6" data-testid="my-leave-adjustments">
          <div className="text-sm font-medium mb-3">Rettifiche della segreteria</div>
          <AdjustmentList items={adjustments} />
        </Card>
      )}

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 border-b border-stone-100 text-sm font-medium flex items-center gap-2">
          <CalendarRange size={14}/> Le mie richieste di permesso <span className="text-stone-400 font-normal">· {all.length}</span>
        </div>
          <RequestList items={all} />
      </Card>

      <Card className="border border-sky-200 rounded-xl shadow-none mt-6" data-testid="portal-medical-list">
        <div className="p-4 border-b border-sky-100 text-sm font-medium flex items-center gap-2 flex-wrap">
          <Stethoscope size={14} className="text-sky-700"/> Visite mediche / Sanità <span className="text-stone-400 font-normal">· {medical.length}</span>
          <span className="text-[11px] font-normal text-stone-500">— non scalano dal monte ore · {medical.filter((r) => r.status === "approvato").reduce((s, r) => s + (r.requested_hours || 0), 0)}h approvate</span>
        </div>
        <RequestList items={medical} empty="Nessuna visita medica segnalata." />
      </Card>

      <NewRequestDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        initialCategory={newCat}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["my-permessi-requests"] });
          qc.invalidateQueries({ queryKey: ["my-medical-requests"] });
          qc.invalidateQueries({ queryKey: ["my-leave-quota"] });
        }}
      />
    </div>
  );
}

function RequestList({ items, empty = "Nessuna richiesta ancora inviata." }) {
  if (items.length === 0) return <div className="p-8 text-center text-sm text-stone-400">{empty}</div>;
  return (
          <ul className="divide-y divide-stone-100">
            {items.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.icon;
              const dateLabel = r.date_from
                ? (r.date_to && r.date_to !== r.date_from ? `${fmtDate(r.date_from)} → ${fmtDate(r.date_to)}` : fmtDate(r.date_from))
                : "—";
              const timeLabel = (r.time_from && r.time_to) ? ` · ${r.time_from}–${r.time_to}` : (r.half_day ? " · mezza giornata" : "");
              return (
                <li key={r.id} data-testid={`portal-permesso-${r.id}`}>
                  <Link to={`/portale/richieste/${r.id}`} className="flex items-center gap-3 p-4 hover:bg-stone-50 transition-colors">
                    <Icon size={18} className={r.status === "pending" ? "text-amber-600 animate-pulse" : r.status === "approvato" ? "text-emerald-600" : r.status === "annullato" ? "text-stone-400" : "text-red-600"}/>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#1C352D] truncate">{r.subject}</div>
                      <div className="text-[11px] text-stone-500 mt-0.5">
                        {dateLabel}{timeLabel} · <b className="text-[#1C352D]">{r.requested_hours || 0}h</b>
                        {r.attachments?.length > 0 && <> · {r.attachments.length} allegat{r.attachments.length > 1 ? "i" : "o"}</>}
                      </div>
                    </div>
                    <Badge className={`text-[10px] rounded-full border ${meta.cls}`}>{meta.label}</Badge>
                    <ArrowRight size={14} className="text-stone-300"/>
                  </Link>
                </li>
              );
            })}
          </ul>
  );
}
